import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Global Reusable Animated CTA Button
 * Follows executive About-page styling (Navy #1a2f6e & Slate #cbd5e1 / #334155),
 * locked 38px height, 205px min-width, traveling sheen, and spring arrow glide.
 *
 * @param {string} [to] - React Router path
 * @param {string} [href] - Anchor link (e.g., '#capabilities')
 * @param {function} [onClick] - Click handler for buttons
 * @param {'primary' | 'secondary'} [variant='primary'] - Button style variant
 * @param {boolean} [showArrow=true] - Whether to show the circular animated arrow bubble
 * @param {'right' | 'down'} [arrowDirection='right'] - Direction of the arrow icon
 * @param {string} [className] - Additional class names
 * @param {object} [style] - Inline style overrides
 * @param {React.ReactNode} children - Button text / label
 */
const CtaButton = ({
  to,
  href,
  onClick,
  variant = 'primary',
  showArrow = true,
  arrowDirection = 'right',
  className = '',
  style = {},
  children,
  ...rest
}) => {
  const baseClass = variant === 'secondary' ? 'sipl-btn-pro-secondary' : 'sipl-btn-pro-primary';
  const combinedClass = `${baseClass} ${className}`.trim();

  const renderArrow = () => {
    if (!showArrow) return null;
    return (
      <span className="sipl-btn-icon-bubble" aria-hidden="true">
        <svg
          width="13"
          height="13"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="sipl-btn-svg"
        >
          {arrowDirection === 'down' ? (
            <path
              d="M8 3V13M4 9L8 13L12 9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            <path
              d="M3 8H13M9 4L13 8L9 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      </span>
    );
  };

  const content = (
    <>
      <span className="sipl-btn-text">{children}</span>
      {renderArrow()}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={combinedClass} style={style} {...rest}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} className={combinedClass} style={style} {...rest}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={combinedClass} style={style} {...rest}>
      {content}
    </button>
  );
};

export default CtaButton;
